import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import { TOUS_LES_SITES } from '../site/context';
import type { Facture, ModeReglement, UniteFacturation } from './useFactures';

/**
 * Bloc « Financement » du dossier apprenant et écran « Financeurs & OPCO »
 * (TF-404). Une prise en charge par contrat d'apprentissage : payeur,
 * référence de prise en charge (DK), coûts pris en charge.
 *
 * L'état du bloc, ce qui manque, le total et son addition, le financeur
 * proposé : tout vient de l'API (`domain/prise-en-charge.ts`). Seul l'aperçu du
 * total pendant la saisie se calcule ici, le temps que l'API le confirme.
 */

export type Payeur = 'FINANCEUR' | 'EMPLOYEUR';
export type EtatPriseEnCharge = 'A_SAISIR' | 'A_COMPLETER' | 'COMPLETE';
export type ManquePriseEnCharge = 'PAYEUR' | 'REFERENCE' | 'COUT_PEDAGOGIQUE';

export interface SituationPriseEnCharge {
  etat: EtatPriseEnCharge;
  libelle: string;
  ton: string;
  manques: ManquePriseEnCharge[];
  detail: string | null;
}

export interface FinanceurChoisissable {
  id: string;
  code: string;
  raisonSociale: string;
  type: string;
  typeLibelle: string;
}

/** Les cinq coûts du bloc, en décimal (« 7392.00 ») ; `null` : inconnu. */
export interface CoutsPriseEnCharge {
  coutPedagogique: string | null;
  fraisRestauration: string | null;
  fraisHebergement: string | null;
  fraisPremierEquipement: string | null;
  fraisMobilite: string | null;
}

/** Apprentissage, contrat de professionnalisation, France Travail (TF-465, `domain/types-facturation.ts`). */
export type TypeFacturation = 'APPRENTISSAGE' | 'PROFESSIONNALISATION' | 'FRANCE_TRAVAIL';

export interface BlocFinancement {
  /** Un contrat d'apprentissage se facture toujours par échéances. */
  typeFacturation: { valeur: TypeFacturation; libelle: string; facturation: string };
  contrat: {
    id: string;
    employeur: { id: string; denomination: string };
    opcoDeclare: string | null;
    dateDebut: string;
    dateFin: string;
    situation: { etat: string; libelle: string; ton: string; detail: string | null };
    numeroDepot: string | null;
    organismeDepot: string | null;
  };
  priseEnCharge:
    | (CoutsPriseEnCharge & {
        id: string;
        payeur: Payeur | null;
        financeur: { id: string; code: string; raisonSociale: string } | null;
        reference: string | null;
        /** Numéro de pro forma (TF-465), porté sur les factures à côté de la référence. */
        numeroProForma: string | null;
        modifieLe: string;
        modifiePar: string | null;
      })
    | null;
  total: { centimes: number | null; texte: string | null; addition: string | null };
  situation: SituationPriseEnCharge;
  financeurPropose: { id: string; raisonSociale: string } | null;
  /** Ce que diront les factures du contrat (TF-406) ; rendu dans le dossier seulement. */
  apercuFacturation: ApercuFacturation | null;
  /** Factures et avoirs du contrat (TF-407), brouillons d'abord. */
  factures: Facture[];
  /** Ce qui empêche de préparer une facture ; `null` : possible. */
  refusPreparation: string | null;
  /** Calendrier des échéances de facturation du contrat (TF-409). */
  echeancier: Echeancier;
  /**
   * Facture à part des frais de 1er équipement (TF-411) ; `null` quand ils ne
   * sont ni pris en charge ni facturés.
   */
  premierEquipement: PremierEquipement | null;
}

/** Où en est la facture des frais de 1er équipement, tout déduit par l'API (`domain/facture-premier-equipement.ts`). */
export interface PremierEquipement {
  /** « 500,00 € » ; `null` s'ils ne sont plus saisis. */
  frais: string | null;
  etat: 'PREVU' | 'A_FACTURER' | 'EN_PREPARATION' | 'FACTURE';
  libelle: string;
  ton: string;
  reste: { centimes: number; texte: string } | null;
  facture: { centimes: number; texte: string; numeros: string[] } | null;
  /** « du 01/09/2026 au 30/11/2026 (1re échéance) » */
  periode: string;
  detail: string | null;
  /** Le geste « Préparer la facture » et ce qui l'empêche ; `null` : pas de geste. */
  preparation: { refus: string | null } | null;
}

// ---------------------------------------------------------------------------
// Échéancier de facturation (TF-409)
// ---------------------------------------------------------------------------

export type EtatEcheance = 'PREVUE' | 'A_FACTURER' | 'FACTUREE' | 'SOLDEE' | 'ANNULEE';
export type EtatEcheancier = 'A_GENERER' | 'PARAMETRE' | 'A_REVOIR';

export interface Echeance {
  id: string;
  rang: number;
  dateDebut: string;
  dateFin: string;
  /** « 3 mois », « 7 jours ». */
  duree: string;
  situation: { etat: EtatEcheance; libelle: string; ton: string; detail: string | null };
  /** Ce qui fige ses dates ; `null` : elle se modifie. */
  verrou: string | null;
  /** Son annulation par la rupture du contrat (TF-414) ; `null` pour une échéance du calendrier. */
  annulation: { le: string; par: string | null; motif: string | null } | null;
  /**
   * Ce que l'échéance facture (TF-410) : facturé, à facturer, prévu, prévision ; pour la
   * dernière, le solde en jours ouvrés (TF-415). Avec son calcul ; `null` pour une échéance
   * annulée.
   */
  montant: MontantEcheance | null;
  /** Sur la prochaine échéance à facturer : ce qui empêche de préparer sa facture. */
  preparation: { refus: string | null } | null;
  /**
   * Son certificat de réalisation (TF-412), à partir de la deuxième échéance :
   * établi ou à établir, et ce qui empêche de l'établir ; `null` sur la première.
   */
  certificat: CertificatEcheance | null;
}

export interface CertificatEcheance {
  etabli: {
    id: string;
    /** « du 01/01/2025 au 30/06/2025 · 6 mois » */
    resume: string;
    /** Date portée sur le certificat, `AAAA-MM-JJ`. */
    date: string;
    genereLe: string;
    generePar: string | null;
    /** Il atteste au-delà de l'échéance, ramenée depuis à la rupture (TF-414) : à régénérer. */
    aRegenerer: string | null;
  } | null;
  refus: string | null;
}

export interface MontantEcheance {
  /**
   * « Facturé », « À facturer », « Prévu », « Prévision » ; pour la dernière : « Solde à
   * facturer », « Solde prévu », « Prévision du solde », « Trop facturé », « Soldé »,
   * « Facturé au mois ».
   */
  libelle: string;
  /** « 1 848,00 € » ; `null` quand il ne se calcule pas. */
  texte: string | null;
  centimes: number | null;
  /** « 7 392,00 € × 6 mois / 12 mois = 3 696,00 €, moins 1 848,00 € déjà facturés » */
  detail: string | null;
  /** La dernière échéance : sa facture est le solde du parcours, en jours ouvrés (TF-415). */
  solde: boolean;
  /** Un écart à corriger : trop facturé, ou dernière échéance facturée au mois. */
  alerte: boolean;
}

export interface Periodicite {
  texte: string | null;
  mois: number | null;
}

/**
 * Le calendrier d'un contrat : ses échéances, son état, sa périodicité et ce
 * qu'il doit couvrir. Tout est déduit par l'API (`domain/echeancier.ts`).
 */
export interface Echeancier {
  echeances: Echeance[];
  situation: { etat: EtatEcheancier; libelle: string; ton: string; detail: string | null };
  periodicite: Periodicite;
  bornes: { debut: string; fin: string; detail: string | null };
  refus: string | null;
  /** Le calendrier face à la rupture du contrat ; `null` sans rupture datée ou sans échéance. */
  rupture: ArretRupture | null;
  modification: { le: string; par: string | null } | null;
  /** Où en est la facturation du coût pédagogique (TF-415 rouverte). */
  avancement: AvancementFacturation;
}

export type EtapeFacturation =
  | 'COUT_A_SAISIR'
  | 'ECHEANCIER_A_GENERER'
  | 'RUPTURE_SANS_DATE'
  | 'ECHEANCIER_A_REVOIR'
  | 'AVOIR_A_ETABLIR'
  | 'A_EMETTRE'
  | 'A_FACTURER'
  | 'A_VENIR'
  | 'TERMINEE';

interface MontantAvancement {
  centimes: number;
  texte: string;
}

/**
 * Où en est la facturation du coût pédagogique d'un contrat : dû sur le
 * parcours — le coût entier, ou sa part jusqu'à la rupture —, facturé, reste,
 * et la prochaine étape. Tout est déduit par l'API
 * (`domain/avancement-facturation.ts`).
 */
export interface AvancementFacturation {
  cout: MontantAvancement | null;
  du: MontantAvancement | null;
  nonFacturable: MontantAvancement | null;
  detailDu: string | null;
  facture: MontantAvancement & { numeros: string[] };
  reste: MontantAvancement | null;
  tropFacture: MontantAvancement | null;
  /** Part du dû facturée, en pour cent entiers ; `null` quand le dû n'est pas connu. */
  part: number | null;
  solde: { rang: number; date: string; libelle: string; montant: string | null } | null;
  etape: {
    etat: EtapeFacturation;
    libelle: string;
    montant: string | null;
    detail: string | null;
    ton: 'success' | 'warning' | 'danger' | 'info';
  };
  resume: string | null;
}

/**
 * Ce que la rupture change au calendrier (TF-414) : ce que « Arrêter le
 * calendrier à la rupture » ferait, ce qui l'en empêche, ou qu'il est arrêté.
 */
export interface ArretRupture {
  /** Date de rupture, `AAAA-MM-JJ`. */
  date: string;
  aFaire: string[];
  obstacles: string[];
  arrete: boolean;
}

/** Ce que l'arrêt a fait : rangs ramené et annulés, et ce qui bloque encore. */
export interface ResultatArretRupture {
  ramenee: number | null;
  annulees: number[];
  obstacles: string[];
  description: string[];
}

export interface EcheanceProposee {
  dateDebut: string;
  dateFin: string;
  duree: string;
  verrou: string | null;
}

export interface PropositionEcheancier {
  echeances: EcheanceProposee[];
  periodicite: Periodicite;
}

/** Le calendrier d'une périodicité ; sans périodicité (`null`, saisie libre, TF-466), ce qui est gardé. */
export function useProposerEcheancier() {
  return useMutation({
    mutationFn: ({ contratId, durees }: { contratId: string; durees: number[] | null }) =>
      appelApi<PropositionEcheancier>(`/financements/contrats/${contratId}/echeances/proposition`, {
        methode: 'POST',
        corps: durees ? { durees } : { libre: true },
      }),
  });
}

/** Une échéance saisie, date à date (TF-466). */
export interface EcheanceSaisie {
  dateDebut: string;
  dateFin: string;
}

/** Enregistre le calendrier entier, échéance par échéance. */
export function useEnregistrerEcheancier() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ contratId, echeances }: { contratId: string; echeances: EcheanceSaisie[] }) =>
      appelApi<{ bloc: BlocFinancement }>(`/financements/contrats/${contratId}/echeances`, {
        methode: 'PUT',
        corps: { echeances },
      }),
    // Le bloc du dossier et la liste de l'écran lisent le même calendrier.
    onSuccess: () => client.invalidateQueries({ queryKey: ['financements'] }),
  });
}

/** Arrête le calendrier à la rupture du contrat, une fois un obstacle levé (TF-414). */
export function useArreterEcheancierALaRupture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (contratId: string) =>
      appelApi<{ arret: ResultatArretRupture; bloc: BlocFinancement }>(
        `/financements/contrats/${contratId}/echeances/rupture`,
        { methode: 'POST', corps: {} },
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['financements'] }),
  });
}

export type ManqueFacture =
  | 'PAYEUR'
  | 'ADRESSE'
  | 'CODE_POSTAL'
  | 'VILLE'
  | 'SIRET'
  | 'REFERENCE'
  | 'DATES_FORMATION';

/**
 * Données que porteront les factures d'un contrat, à ce jour : la
 * correspondance de l'émission (`domain/donnees-facture.ts`), lue par l'API.
 */
export interface ApercuFacturation {
  prete: boolean;
  detail: string | null;
  manques: ManqueFacture[];
  client: {
    nom: string;
    typeLibelle: string;
    fiche: { nature: 'FINANCEUR' | 'ENTREPRISE'; id: string };
    adresse: string | null;
    siret: string | null;
    email: string | null;
    delaiPaiementJours: number;
    detail: string | null;
  } | null;
  apprenant: string;
  formation: string | null;
  datesFormation: { texte: string | null; provenance: string | null };
  /** `provenance` : « émargement, feuilles clôturées », ou les déclarations des formateurs d'un accord aux heures. */
  heures: { texte: string; avertissement: string | null; jusquA: string; provenance: string };
  reference: string | null;
  referenceExigee: boolean;
  /** Numéro de pro forma (TF-465) ; `null` s'il n'est pas saisi. */
  numeroProForma: string | null;
}

/**
 * Ce qui empêchera d'émettre les factures d'un contrat ou d'un accord (TF-406),
 * lu par l'API sans ouvrir le dossier. Le texte ne redit pas le payeur ni la
 * référence, que l'état du bloc dit déjà.
 */
export interface EtatDonneesFacture {
  prete: boolean;
  manques: ManqueFacture[];
  /** La fiche du client à compléter : une fois pour tous ses dossiers. */
  ficheClient: {
    nature: 'FINANCEUR' | 'ENTREPRISE';
    id: string;
    nom: string;
    typeLibelle: string;
    manques: string[];
    detail: string;
  } | null;
  /** « Dates de formation à préciser. » ; `null` sinon. */
  detailDossier: string | null;
}

export interface FinancementDossier {
  apprenantId: string;
  mode: 'CFA' | 'OF';
  /** Un bloc par contrat d'apprentissage ; vide en OF. */
  blocs: BlocFinancement[];
  /** L'accord de financement d'un parcours OF (TF-417) ; `null` en CFA. */
  accord: AccordFinancement | null;
  financeurs: FinanceurChoisissable[];
  /** Sans les mentions de l'émetteur, aucune facture ne s'émet. */
  emetteur: { complet: boolean; manques: string[]; detail: string | null };
  referentielFacture: {
    unites: { valeur: UniteFacturation; libelle: string }[];
    tauxTva: { valeur: string; libelle: string }[];
    modesReglement: { valeur: ModeReglement; libelle: string }[];
  };
  /** Ce que propose la génération d'un échéancier (TF-409). */
  referentielEcheancier: {
    dureesProposees: number[];
    dureeParDefaut: number;
    dureeMaximum: number;
    dureesMaximum: number;
    echeancesMaximum: number;
  };
}

export interface SaisiePriseEnCharge extends CoutsPriseEnCharge {
  payeur: { type: 'FINANCEUR'; financeurId: string } | { type: 'EMPLOYEUR' } | null;
  reference: string | null;
  numeroProForma: string | null;
}

/**
 * Une ligne de « Financeurs & OPCO » : un contrat d'apprentissage né, ou un
 * dossier OF et son accord de financement (TF-417).
 */
export interface LigneFinancement {
  /** Le contrat, ou `accord:<dossier>`. */
  cle: string;
  nature: 'APPRENTISSAGE' | 'ACCORD_OF';
  /** `null` pour un dossier OF sans accord (TF-465). */
  typeFacturation: TypeFacturation | null;
  contratId: string | null;
  apprenant: { id: string; nom: string; prenom: string };
  formation: string;
  siteCode: string;
  employeur: string | null;
  dateDebut: string | null;
  dateFin: string | null;
  /** État du contrat ; en OF, la nature de l'accord. */
  situationContrat: { libelle: string; ton: string };
  payeur: string | null;
  reference: string | null;
  /** Total pris en charge ; en OF, le montant maximal de l'accord. */
  total: { centimes: number | null; texte: string | null };
  situation: { etat: EtatPriseEnCharge | EtatAccord; libelle: string; ton: string; manques: string[]; detail: string | null };
  /** État du calendrier des échéances ; `detail` : « 8 échéances, tous les 3 mois ». `null` en OF. */
  echeancier: { etat: EtatEcheancier; libelle: string; ton: string; detail: string | null } | null;
  /** En OF : « 15,00 €/h × 800 h = 12 000,00 € », et comment les factures se calculent. */
  accord: { calcul: string | null; facturation: string | null } | null;
  /**
   * Échéances arrivées à leur dernier jour sans facture émise (TF-413), mois
   * d'un contrat de professionnalisation à facturer (TF-416), fin d'un
   * parcours France Travail à facturer (TF-418, 0 ou 1) : la lecture de
   * l'alerte « Facture à émettre » — « Échéance n° 2 à facturer »,
   * « Octobre 2026 à facturer », « Fin de parcours à facturer ».
   */
  aFacturer: {
    echeances: number;
    mois: number;
    finParcours: number;
    texte: string | null;
    /** Le solde du parcours en fait partie (TF-415). */
    solde: boolean;
    /** Rien à facturer : un trop-facturé à annuler par un avoir partiel. */
    avoir: boolean;
    /** Frais de 1er équipement à facturer, 0 ou 1. */
    premierEquipement: number;
  };
  /** « Facturé 3 696,00 € sur 7 392,00 € · solde le 31/08/2027 » ; `null` en OF. */
  avancement: {
    part: number | null;
    resume: string | null;
    nonFacturable: { centimes: number; texte: string } | null;
  } | null;
  /** Ce qui empêchera d'émettre ses factures ; `null` pour un parcours OF sans accord. */
  facture: EtatDonneesFacture | null;
}

/** Fiche d'un client des factures à qui manque une coordonnée, et les dossiers qui l'attendent. */
export interface FicheClientACompleter {
  nature: 'FINANCEUR' | 'ENTREPRISE';
  id: string;
  nom: string;
  typeLibelle: string;
  manques: string[];
  detail: string;
  dossiers: number;
}

export interface ListeFinancements {
  lignes: LigneFinancement[];
  /** Les valeurs du filtre « Type de facturation » (TF-465). */
  filtres: { typesFacturation: { valeur: TypeFacturation; libelle: string }[] };
  /** Ce qui empêchera d'émettre au-delà d'un dossier : mentions de l'émetteur, fiches des clients (TF-406). */
  preparation: {
    emetteur: { complet: boolean; manques: string[]; detail: string | null };
    fichesClients: FicheClientACompleter[];
  };
  compteurs: {
    contrats: number;
    aSaisir: number;
    aCompleter: number;
    completes: number;
    sansReference: number;
    echeanciersAGenerer: number;
    echeanciersARevoir: number;
    echeancesAFacturer: number;
    contratsAFacturer: number;
    /** Mois de contrats de professionnalisation à facturer (TF-416). */
    moisAFacturer: number;
    /** Fins de parcours France Travail à facturer (TF-418). */
    finsDeParcoursAFacturer: number;
    /** Parcours OF qui ont un mois ou leur fin de parcours à facturer. */
    parcoursAFacturer: number;
    /** Contrats dont les frais de 1er équipement sont à facturer. */
    premiersEquipementsAFacturer: number;
    /** Contrats trop facturés : un avoir partiel à établir. */
    avoirsAEtablir: number;
    /** Contrats et accords dont les factures ne pourraient pas encore être émises (TF-406). */
    donneesFactureIncompletes: number;
    /** Dossiers OF du périmètre et leur accord (TF-417). */
    parcoursOf: { dossiers: number; sansAccord: number; aCompleter: number; complets: number };
    totalPrisEnCharge: string | null;
  };
}

export const LIBELLES_COUTS: { cle: keyof CoutsPriseEnCharge; libelle: string }[] = [
  { cle: 'coutPedagogique', libelle: 'Coût pédagogique' },
  { cle: 'fraisRestauration', libelle: 'Frais de restauration' },
  { cle: 'fraisHebergement', libelle: 'Frais d’hébergement' },
  { cle: 'fraisPremierEquipement', libelle: 'Frais de 1er équipement' },
  { cle: 'fraisMobilite', libelle: 'Frais de mobilité' },
];

export function useFinancementDossier(apprenantId: string | undefined) {
  return useQuery({
    queryKey: ['financements', 'dossier', apprenantId],
    queryFn: () => appelApi<FinancementDossier>(`/financements/dossiers/${apprenantId}`),
    enabled: Boolean(apprenantId),
  });
}

export function useEnregistrerPriseEnCharge() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ contratId, saisie }: { contratId: string; saisie: SaisiePriseEnCharge }) =>
      appelApi<{ bloc: BlocFinancement }>(`/financements/contrats/${contratId}`, {
        methode: 'PUT',
        corps: saisie,
      }),
    // Le bloc du dossier et la liste de l'écran lisent la même prise en charge.
    onSuccess: () => client.invalidateQueries({ queryKey: ['financements'] }),
  });
}

// ---------------------------------------------------------------------------
// Échéances du mois (TF-465)
// ---------------------------------------------------------------------------

export type EtatEcheanceProgrammee =
  | 'A_VENIR'
  | 'A_FACTURER'
  | 'EN_PREPARATION'
  | 'EN_ATTENTE'
  | 'FACTUREE'
  | 'RIEN_A_FACTURER'
  | 'AVOIR_A_ETABLIR';

/** Une échéance programmée, tout type confondu : état et montant sont ceux de son bloc Financement. */
export interface EcheanceProgrammee {
  cle: string;
  /** `AAAA-MM-JJ` : le jour où la facture devient due. */
  date: string;
  typeFacturation: TypeFacturation;
  typeLibelle: string;
  libelle: string;
  periode: string;
  etat: EtatEcheanceProgrammee;
  etatLibelle: string;
  ton: string;
  detail: string | null;
  montant: { libelle: string; centimes: number; texte: string } | null;
  dossier: {
    apprenant: { id: string; nom: string; prenom: string };
    formation: string;
    siteCode: string;
    payeur: string | null;
    employeur: string | null;
  };
}

export interface EcheancesDuMois {
  mois: { cle: string; libelle: string; precedent: string; suivant: string; courant: boolean };
  lignes: EcheanceProgrammee[];
  compteurs: {
    total: { nombre: number; montant: string | null; sansMontant: number };
    parType: { type: TypeFacturation; libelle: string; nombre: number; montant: string | null }[];
    parEtat: { etat: EtatEcheanceProgrammee; libelle: string; nombre: number }[];
  };
  /** Échéances des mois précédents encore à facturer ou en préparation. */
  anterieures: number;
}

/** Les échéances programmées d'un mois (`AAAA-MM`), le mois en cours sans mois dit. */
export function useEcheancesDuMois(site: string, mois: string | null) {
  return useQuery({
    queryKey: ['financements', 'echeances', site, mois],
    queryFn: () => {
      const parametres = new URLSearchParams();
      if (site && site !== TOUS_LES_SITES) parametres.set('site', site);
      if (mois) parametres.set('mois', mois);
      const requete = parametres.toString();
      return appelApi<EcheancesDuMois>(`/financements/echeances${requete ? `?${requete}` : ''}`);
    },
    placeholderData: keepPreviousData,
  });
}

export function useFinancements(site: string) {
  return useQuery({
    queryKey: ['financements', 'liste', site],
    queryFn: () =>
      appelApi<ListeFinancements>(
        `/financements${site && site !== TOUS_LES_SITES ? `?site=${encodeURIComponent(site)}` : ''}`,
      ),
  });
}

// ---------------------------------------------------------------------------
// Accord de financement d'un parcours OF (TF-417)
// ---------------------------------------------------------------------------

export type NatureAccord = 'PROFESSIONNALISATION' | 'FRANCE_TRAVAIL';
export type EtatAccord = 'SANS_ACCORD' | 'A_COMPLETER' | 'COMPLETE';

/** Un employeur du répertoire de l'antenne, tel que le choix de l'accord le présente. */
export interface EmployeurAccord {
  id: string;
  denomination: string;
  siret: string;
  ville: string | null;
  opco: string | null;
  /** Le financeur que l'OPCO de la fiche désigne. */
  financeurPropose: { id: string; raisonSociale: string } | null;
}

/**
 * L'accord d'un dossier OF : contrat de professionnalisation ou France
 * Travail, ce qui lui manque, son montant maximal et ce que la première
 * saisie propose. Tout est déduit par l'API (`domain/accord-financement.ts`).
 */
export interface AccordFinancement {
  dossier: {
    formation: { titre: string; code: string; heures: number } | null;
    session: { libelle: string; dateDebut: string; dateFin: string } | null;
    dateEntree: string | null;
    dateFinPrevue: string | null;
  };
  accord: {
    id: string;
    nature: NatureAccord;
    natureLibelle: string;
    facturation: string;
    libelleDates: string;
    payeur: Payeur | null;
    financeur: { id: string; code: string; raisonSociale: string } | null;
    employeur: EmployeurAccord | null;
    reference: string | null;
    numeroProForma: string | null;
    tauxHoraire: string | null;
    volumeHeures: string | null;
    tauxTexte: string | null;
    volumeTexte: string | null;
    dateDebut: string | null;
    dateFin: string | null;
    periode: string | null;
    factures: number;
    modifieLe: string;
    modifiePar: string | null;
  } | null;
  situation: { etat: EtatAccord; libelle: string; ton: string; manques: string[]; detail: string | null };
  montantMaximal: { centimes: number; texte: string; calcul: string } | null;
  depassementProgramme: string | null;
  propositions: {
    dateDebut: string | null;
    dateFin: string | null;
    provenanceDates: string | null;
    employeur: EmployeurAccord | null;
    financeurFranceTravail: { id: string; raisonSociale: string } | null;
  };
  apercuFacturation: ApercuFacturation | null;
  /** Facturation mensuelle d'un contrat de professionnalisation (TF-416) ; `null` sinon. */
  facturation: FacturationMensuelle | null;
  /** Facture unique de fin de parcours France Travail (TF-418) ; `null` sinon. */
  finParcours: FacturationFinParcours | null;
  /** Factures et avoirs de l'accord, brouillons d'abord. */
  factures: Facture[];
  /** Le taux ne change plus : des heures sont facturées à ce taux. */
  refusTaux: string | null;
  natures: { valeur: NatureAccord; libelle: string; facturation: string; dates: string; employeurExige: boolean }[];
  refusNature: string | null;
}

export type EtatMoisFacture =
  | 'EN_COURS'
  | 'A_FACTURER'
  | 'COMPLEMENT'
  | 'EN_PREPARATION'
  | 'FACTURE'
  | 'TROP_FACTURE'
  | 'EN_ATTENTE_DECLARATION'
  | 'EN_ATTENTE_CLOTURE'
  | 'SANS_HEURE'
  | 'VOLUME_ATTEINT';

/**
 * Un mois commencé de l'accord : heures réalisées — déclarées par les formateurs
 * (TF-421), émargées avant leur mise en service —, facturé, à facturer (TF-416).
 * Tout vient de l'API.
 */
export interface MoisFacture {
  /** `AAAA-MM` */
  cle: string;
  libelle: string;
  periode: string;
  partiel: boolean;
  etat: EtatMoisFacture;
  etatLibelle: string;
  ton: string;
  heuresRealisees: string;
  /** « déclarations des formateurs », « émargement, feuilles clôturées ». */
  source: string;
  /** Le repère émargé, quand la source n'est pas l'émargement seul. */
  heuresEmargees: string | null;
  /** Qui a déclaré quoi ; `null` sur l'émargement seul. */
  origine: string | null;
  /** « Déclaration attendue : Jean Dupont (octobre 2026). » */
  declarationsAttendues: string | null;
  feuillesOuvertes: string | null;
  facture: { heures: string; montant: string; numeros: string[] } | null;
  aFacturer: { heures: string; montant: string | null } | null;
  detail: string | null;
  preparation: { refus: string | null } | null;
}

export interface FacturationMensuelle {
  /** Du plus récent au plus ancien. */
  mois: MoisFacture[];
  moisAVenir: number;
  cumul: {
    heuresFacturees: string;
    montantFacture: string;
    volume: string | null;
    reste: string | null;
    part: number | null;
  };
  source: string;
  message: string | null;
}

export type EtatFinParcours =
  | 'SANS_DATES'
  | 'EN_COURS'
  | 'EN_ATTENTE_DECLARATION'
  | 'EN_ATTENTE_CLOTURE'
  | 'A_FACTURER'
  | 'COMPLEMENT'
  | 'EN_PREPARATION'
  | 'FACTURE'
  | 'TROP_FACTURE'
  | 'SANS_HEURE'
  | 'VOLUME_ATTEINT';

/** La facture unique d'un parcours France Travail : heures, facturé, à facturer (TF-418). Tout vient de l'API. */
export interface FacturationFinParcours {
  /** « du 01/09/2026 au 30/06/2027 » ; `null` sans dates. */
  periode: string | null;
  /** « 30/06/2027 » ; `null` sans dates. */
  facturableLe: string | null;
  etat: EtatFinParcours;
  etatLibelle: string;
  ton: string;
  heuresRealisees: string;
  heuresEmargees: string | null;
  origine: string | null;
  declarationsAttendues: string | null;
  feuillesOuvertes: string | null;
  facture: { heures: string; montant: string; numeros: string[] } | null;
  aFacturer: { heures: string; montant: string | null } | null;
  detail: string | null;
  preparation: { refus: string | null } | null;
  cumul: FacturationMensuelle['cumul'];
  source: string;
}

export interface SaisieAccord {
  nature: NatureAccord;
  payeur: { type: 'FINANCEUR'; financeurId: string } | { type: 'EMPLOYEUR' } | null;
  employeurId: string | null;
  reference: string | null;
  numeroProForma: string | null;
  tauxHoraire: string | null;
  volumeHeures: string | null;
  /** `AAAA-MM-JJ` */
  dateDebut: string | null;
  dateFin: string | null;
}

export function useEnregistrerAccord() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ apprenantId, saisie }: { apprenantId: string; saisie: SaisieAccord }) =>
      appelApi<{ accord: AccordFinancement }>(`/financements/dossiers/${apprenantId}/accord`, {
        methode: 'PUT',
        corps: saisie,
      }),
    // Le bloc du dossier et la liste de l'écran lisent le même accord.
    onSuccess: () => client.invalidateQueries({ queryKey: ['financements'] }),
  });
}

/**
 * Les employeurs de l'antenne du dossier, filtrés ensuite à l'écran par nom,
 * SIRET ou ville. Chargés seulement à l'ouverture de la saisie.
 */
export function useEmployeursAccord(apprenantId: string, actif: boolean) {
  return useQuery({
    queryKey: ['financements', 'employeurs', apprenantId],
    queryFn: () => appelApi<{ employeurs: EmployeurAccord[] }>(`/financements/dossiers/${apprenantId}/employeurs`),
    enabled: actif,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Fiches des financeurs (TF-406)
// ---------------------------------------------------------------------------

export type TypeFinanceur = 'OPCO' | 'FRANCE_TRAVAIL' | 'AUTRE';
export type ManqueClient = 'ADRESSE' | 'CODE_POSTAL' | 'VILLE' | 'SIRET';

/** Une fiche telle que l'API la rend : état et adresse imprimée compris. */
export interface Financeur {
  id: string;
  code: string;
  type: TypeFinanceur;
  typeLibelle: string;
  raisonSociale: string;
  siret: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  emailFacturation: string | null;
  delaiPaiementJours: number;
  actif: boolean;
  adresseImprimee: string | null;
  etat: { pret: boolean; libelle: string; ton: string; manques: ManqueClient[]; detail: string | null };
  contrats: number;
  factures: number;
  modifieLe: string;
}

export interface ListeFinanceurs {
  financeurs: Financeur[];
  compteurs: { actifs: number; prets: number; aCompleter: number };
  types: { valeur: TypeFinanceur; libelle: string }[];
}

export interface SaisieFinanceur {
  type: TypeFinanceur;
  raisonSociale: string;
  siret: string;
  adresse: string;
  codePostal: string;
  ville: string;
  emailFacturation: string;
  delaiPaiementJours: string;
  actif: boolean;
}

export function useFinanceurs() {
  return useQuery({
    queryKey: ['financements', 'financeurs'],
    queryFn: () => appelApi<ListeFinanceurs>('/financements/financeurs'),
  });
}

/** Crée une fiche (sans `id`) ou la corrige en entier. */
export function useEnregistrerFinanceur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saisie }: { id: string | null; saisie: SaisieFinanceur }) =>
      appelApi<{ financeur: Financeur }>(id ? `/financements/financeurs/${id}` : '/financements/financeurs', {
        methode: id ? 'PUT' : 'POST',
        corps: saisie,
      }),
    // La fiche nourrit le choix du payeur et l'aperçu des factures de chaque dossier.
    onSuccess: () => client.invalidateQueries({ queryKey: ['financements'] }),
  });
}

// ---------------------------------------------------------------------------
// Saisie des montants
// ---------------------------------------------------------------------------

const FORME_MONTANT = /^\d+(?:\.\d{1,2})?$/;

/**
 * Centimes d'un montant saisi à la française (« 7 392,00 », « 500 ») ; `null`
 * s'il est vide, `undefined` s'il est illisible. Sert l'aperçu du total ;
 * l'API refait la validation.
 */
export function centimesSaisis(texte: string): number | null | undefined {
  const normalise = texte.replace(/[\s  ]/g, '').replace(',', '.').replace(/€$/, '');
  if (normalise === '') return null;
  if (!FORME_MONTANT.test(normalise)) return undefined;
  const [entiers = '0', decimales = ''] = normalise.split('.');
  return Number(entiers) * 100 + Number(decimales.padEnd(2, '0'));
}

/** Montant de l'API (« 7392.00 ») tel qu'on le corrige dans un champ : « 7392,00 ». */
export function montantEnSaisie(valeur: string | null): string {
  return valeur === null ? '' : valeur.replace('.', ',');
}

/** « 7 392,00 € », pour l'affichage. */
export function formaterCentimes(centimes: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(centimes / 100);
}
